import { useState } from "react";
import { ArrowLeft, ArrowRight, Check, KeyRound, Server, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "../api";

type SetupState = {
  setupKey: string;
  studioName: string;
  tenantSlug: string;
  baseUrl: string;
  defaultTimezone: string;
  issuer: string;
  clientId: string;
  clientSecret: string;
  adminClientId: string;
  adminClientSecret: string;
  managerRole: string;
};

const initial: SetupState = {
  setupKey: "",
  studioName: "EdgeFox Studios",
  tenantSlug: "edgefox",
  baseUrl: window.location.origin,
  defaultTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  issuer: "https://auth.edgefoxenterprise.net/realms/edgefox",
  clientId: "meridian",
  clientSecret: "",
  adminClientId: "meridian-directory",
  adminClientSecret: "",
  managerRole: "calendar-manager",
};

const steps = [
  { title: "Welcome to Meridian", icon: Sparkles },
  { title: "Workspace identity", icon: ShieldCheck },
  { title: "Directory service", icon: Server },
  { title: "Ready to launch", icon: Check },
];

function safeHostname(value: string): string {
  try {
    return new URL(value).hostname || "Not configured";
  } catch {
    return "Invalid issuer URL";
  }
}

function Field({
  label,
  hint,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="field-group">
      <span className="field-label">{label}</span>
      <input className="text-field" type={type} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      {hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

export function SetupWizard() {
  const [step, setStep] = useState(0);
  const [state, setState] = useState(initial);
  const [saving, setSaving] = useState(false);
  const update = (key: keyof SetupState) => (value: string) => setState((current) => ({ ...current, [key]: value }));

  async function finish() {
    setSaving(true);
    try {
      await api<{ tenant: { slug: string } }>("/api/setup", {
        method: "POST",
        body: JSON.stringify(state),
      });
      toast.success("Meridian is ready. Connecting to your workspace…");
      window.setTimeout(() => {
        window.location.href = `/auth/login?tenant=${encodeURIComponent(state.tenantSlug)}`;
      }, 700);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Setup could not be completed.");
      setSaving(false);
    }
  }

  return (
    <main className="setup-shell" data-theme="midnight">
      <section className="setup-panel">
        <aside className="setup-aside">
          <div className="brand-mark large">M</div>
          <div>
            <p className="eyebrow">Meridian Calendar</p>
            <h1>One clear view of every calendar your studio runs on.</h1>
            <p>Secure identity, reliable subscriptions, team permissions, and a master feed—without a pile of disconnected calendars.</p>
          </div>
          <div className="setup-progress" aria-label="Setup progress">
            {steps.map((item, index) => (
              <div className={`setup-progress-item ${index === step ? "active" : ""} ${index < step ? "done" : ""}`} key={item.title}>
                <span>{index < step ? <Check size={15} /> : index + 1}</span>
                <div><strong>{item.title}</strong><small>{index === 0 ? "Studio basics" : index === 1 ? "OIDC connection" : index === 2 ? "Attribute synchronization" : "Review and continue"}</small></div>
              </div>
            ))}
          </div>
        </aside>

        <div className="setup-content">
          <div className="setup-step-heading">
            {(() => { const Icon = steps[step].icon; return <Icon size={20} />; })()}
            <span>Step {step + 1} of {steps.length}</span>
          </div>

          {step === 0 ? (
            <div className="setup-form">
              <h2>Let’s name the first workspace.</h2>
              <p>The first tenant is special: members with <code>app-superadmin</code> become platform admins. In later tenants, that role is tenant-scoped.</p>
              <div className="form-grid two">
                <Field label="Studio or workspace name" value={state.studioName} onChange={update("studioName")} />
                <Field label="Workspace slug" value={state.tenantSlug} onChange={update("tenantSlug")} hint="Lowercase letters, numbers, and single hyphens." />
              </div>
              <Field label="Public application URL" value={state.baseUrl} onChange={update("baseUrl")} hint="Used for OIDC callbacks and the generated masterCalendar link." />
              <Field label="Default timezone" value={state.defaultTimezone} onChange={update("defaultTimezone")} hint="Each user can still detect or override their own timezone." />
              <Field label="Setup key" type="password" value={state.setupKey} onChange={update("setupKey")} hint="Printed once in the Pterodactyl console and deleted after setup." />
            </div>
          ) : null}

          {step === 1 ? (
            <div className="setup-form">
              <h2>Connect your OIDC application.</h2>
              <p>Meridian uses Authorization Code + PKCE. Keep the default scopes simple—roles and groups should be mapped into token claims, not requested through an invalid <code>groups</code> scope.</p>
              <Field label="Issuer URL" value={state.issuer} onChange={update("issuer")} />
              <div className="form-grid two">
                <Field label="Client ID" value={state.clientId} onChange={update("clientId")} />
                <Field label="Client secret" type="password" value={state.clientSecret} onChange={update("clientSecret")} hint="Leave blank only for a public PKCE client." />
              </div>
              <div className="callback-card"><KeyRound size={18} /><div><strong>Callback URL</strong><code>{state.baseUrl.replace(/\/$/, "")}/auth/callback</code></div></div>
              <div className="callback-card"><ShieldCheck size={18} /><div><strong>Backchannel logout URL</strong><code>{state.baseUrl.replace(/\/$/, "")}/auth/backchannel</code></div></div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="setup-form">
              <h2>Authorize directory synchronization.</h2>
              <p>This service account reads <code>taskCalendar</code>, avatar attributes, and active status, then writes <code>masterCalendar</code> on first login.</p>
              <div className="form-grid two">
                <Field label="Service account client ID" value={state.adminClientId} onChange={update("adminClientId")} />
                <Field label="Service account secret" type="password" value={state.adminClientSecret} onChange={update("adminClientSecret")} />
              </div>
              <Field label="Calendar manager role" value={state.managerRole} onChange={update("managerRole")} />
              <div className="permission-note">
                <ShieldCheck size={20} />
                <div><strong>Required Keycloak service-account roles</strong><p><code>view-users</code>, <code>query-users</code>, and <code>manage-users</code> in the tenant realm. Meridian never needs realm-admin.</p></div>
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            <div className="setup-form review-step">
              <div className="review-orbit"><div className="brand-mark large">M</div></div>
              <h2>Everything is ready for first login.</h2>
              <p>Meridian will create the initial tenant, store secrets with AES-256-GCM, connect the directory, and then send you through OIDC.</p>
              <div className="review-grid">
                <div><span>Workspace</span><strong>{state.studioName}</strong><small>{state.tenantSlug}</small></div>
                <div><span>Identity provider</span><strong>{safeHostname(state.issuer)}</strong><small>{state.clientId}</small></div>
                <div><span>Timezone</span><strong>{state.defaultTimezone}</strong><small>Auto-detect enabled</small></div>
              </div>
            </div>
          ) : null}

          <footer className="setup-actions">
            <Button variant="ghost" onClick={() => setStep((value) => Math.max(0, value - 1))} disabled={step === 0 || saving}><ArrowLeft /> Back</Button>
            {step < steps.length - 1 ? (
              <Button onClick={() => setStep((value) => Math.min(steps.length - 1, value + 1))}>Continue <ArrowRight /></Button>
            ) : (
              <Button onClick={finish} disabled={saving}>{saving ? "Creating workspace…" : "Create Meridian workspace"} <Sparkles /></Button>
            )}
          </footer>
        </div>
      </section>
    </main>
  );
}
